import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getDeploymentAdmins,
  getDeploymentsUsers,
  getDeploymentUsers,
  getUser,
  getWorkspaceAdmins,
  getWorkspaceUsers,
} from "@/api/resources/users/api";
import { type GetDeploymentAdminsProps } from "@/api/resources/users/types";
import i18n from "@/providers/i18n";

const entity = i18n.t("user");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("users");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const usersQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "users"],
  lists: () => [...usersQueryKeys.all(), "list"],
  listWorkspaceUsers: (workspace_id?: string) => [...usersQueryKeys.lists(), "workspace", workspace_id],
  listDeploymentUsers: (deployment_id?: string) => [...usersQueryKeys.lists(), "deployment", deployment_id],
  listDeploymentsUsers: (deployment_ids: string[]) => [...usersQueryKeys.lists(), "deployments", deployment_ids],
  listWorkspaceAdmins: (workspace_id?: string) => [...usersQueryKeys.lists(), "workspace-admins", workspace_id],
  listDeploymentAdmins: (props: GetDeploymentAdminsProps) => [
    ...usersQueryKeys.lists(),
    "deployment-admins",
    props.filteredDeployments,
    props.globalFilteredDeployments,
  ],
  byId: (id?: string) => [...usersQueryKeys.all(), "byId", id],
};

export const usersQueries = {
  listWorkspaceUsers: (workspace_id?: string) =>
    queryOptions({
      queryKey: [...usersQueryKeys.listWorkspaceUsers(workspace_id)],
      queryFn: workspace_id ? () => getWorkspaceUsers(workspace_id) : skipToken,

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listDeploymentUsers: (deployment_id?: string) =>
    queryOptions({
      queryKey: [...usersQueryKeys.listDeploymentUsers(deployment_id)],
      queryFn: deployment_id ? () => getDeploymentUsers(deployment_id) : skipToken,

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listDeploymentsUsers: (deployment_ids: string[]) =>
    queryOptions({
      queryKey: [...usersQueryKeys.listDeploymentsUsers(deployment_ids)],
      queryFn: () => getDeploymentsUsers(deployment_ids),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listWorkspaceAdmins: (workspace_id?: string) =>
    queryOptions({
      queryKey: [...usersQueryKeys.listWorkspaceAdmins(workspace_id)],
      queryFn: workspace_id ? () => getWorkspaceAdmins(workspace_id) : skipToken,

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listDeploymentAdmins: (props: GetDeploymentAdminsProps) =>
    queryOptions({
      queryKey: [...usersQueryKeys.listDeploymentAdmins(props)],
      queryFn: () => getDeploymentAdmins(props),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...usersQueryKeys.byId(id)],
      queryFn: id ? () => getUser({ id }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
